import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useCurrency } from '@/lib/currency-context';
import { Package, User, MapPin, CreditCard, Truck, Clock, Store, FileText, X, Save, ExternalLink, Receipt, CheckCircle2, AlertCircle, Loader2, Mail, Trash2, Ban } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { formatPaymentMethodLabel, resolveOrderPaymentMethod } from '@/lib/payment-methods';
import { formatSelectedVariant } from '@/lib/variant';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
  processing: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  shipped: 'bg-purple-100 text-purple-800 border-purple-300',
  delivered: 'bg-green-100 text-green-800 border-green-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
};

const paymentStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-100 text-gray-800',
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
      <Icon className="w-4 h-4" />
      {title}
    </div>
    {children}
  </div>
);

const InfoRow = ({ label, value, mono }) => (
  <div className="flex justify-between items-start py-1.5 text-sm">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className={`text-right font-medium text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>
      {value || '-'}
    </span>
  </div>
);

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const OrderDetailModal = ({ orderId, open, onClose, onUpdate, variant = 'admin' }) => {
  const { formatPrice } = useCurrency();
  const ordersApiBase = variant === 'seller' ? '/seller/orders' : '/admin/orders';
  const isSellerView = variant === 'seller';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [parasutSending, setParasutSending] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSendStatus, setEmailSendStatus] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (open && orderId) {
      setEmailSendStatus(null);
      setCancelReason('');
      fetchOrder();
    }
  }, [open, orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${ordersApiBase}/${orderId}`);
      setOrder(res.data);
      setEditData({
        status: res.data.status,
        payment_status: res.data.payment_status,
        tracking_number: res.data.tracking_number || '',
        carrier: res.data.carrier || '',
        admin_notes: res.data.admin_notes || '',
      });
    } catch (err) {
      toast.error('Siparis detaylari yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`${ordersApiBase}/${orderId}`, editData);
      toast.success('Siparis guncellendi');
      setEditing(false);
      fetchOrder();
      onUpdate?.();
    } catch (err) {
      toast.error('Guncelleme hatasi');
    } finally {
      setSaving(false);
    }
  };

  const handleResendOrderEmail = async () => {
    if (!orderId) return;
    const targetEmail = order?.customer?.email;
    if (!targetEmail) {
      setEmailSendStatus({
        success: false,
        message: 'Müşteri e-posta adresi bulunamadı',
        email: null,
      });
      toast.error('Müşteri e-posta adresi bulunamadı');
      return;
    }
    try {
      setEmailSending(true);
      setEmailSendStatus(null);
      const res = await api.post(`${ordersApiBase}/${orderId}/resend-confirmation-email`);
      const data = res.data || {};
      setEmailSendStatus({
        success: Boolean(data.success),
        message: data.message || (data.success ? 'E-posta gönderildi' : 'E-posta gönderilemedi'),
        email: data.email || targetEmail,
        at: new Date().toISOString(),
      });
      if (data.success) {
        toast.success(data.message || 'Sipariş bilgileri e-posta ile gönderildi');
      } else {
        toast.error(data.message || 'E-posta gönderilemedi');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'E-posta gönderilemedi';
      setEmailSendStatus({
        success: false,
        message: typeof msg === 'string' ? msg : 'E-posta gönderilemedi',
        email: targetEmail,
        at: new Date().toISOString(),
      });
      toast.error(typeof msg === 'string' ? msg : 'E-posta gönderilemedi');
    } finally {
      setEmailSending(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderId || isSellerView) return;
    try {
      setDeleting(true);
      await api.delete(`/admin/orders/${orderId}`);
      toast.success('Sipariş silindi');
      setDeleteConfirmOpen(false);
      onUpdate?.();
      onClose?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Sipariş silinemedi');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!orderId || isSellerView) return;
    try {
      setCancelling(true);
      const res = await api.post(`/admin/orders/${orderId}/cancel`, {
        reason: cancelReason.trim() || undefined,
      });
      const data = res.data || {};
      if (data.email_sent) {
        toast.success(`Sipariş iptal edildi — e-posta gönderildi${data.email ? ` (${data.email})` : ''}`);
      } else {
        toast.success('Sipariş iptal edildi');
        if (data.email_message) {
          toast.warning(`E-posta gönderilemedi: ${data.email_message}`);
        }
      }
      setCancelConfirmOpen(false);
      setCancelReason('');
      await fetchOrder();
      onUpdate?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Sipariş iptal edilemedi');
    } finally {
      setCancelling(false);
    }
  };

  const isCancelled = String(order?.status || '').toLowerCase() === 'cancelled';

  const handleParasutSend = async () => {
    if (!orderId) return;
    try {
      setParasutSending(true);
      const res = await api.post(`${ordersApiBase}/${orderId}/parasut-sync`);
      // Geliştirici: Network sekmesinde aynı POST isteğinin yanıtını görebilirsiniz.
      console.log('[Paraşüt manuel gönder] yanıt:', res.data);
      if (res.data?.already_synced) {
        toast.info(res.data?.result?.message || 'Bu sipariş zaten Paraşüt\'te kayıtlı.');
        await fetchOrder();
        onUpdate?.();
        return;
      }
      if (res.data?.order) {
        setOrder(res.data.order);
      } else {
        await fetchOrder();
      }
      toast.success('Paraşüt\'e gönderildi.');
      onUpdate?.();
    } catch (err) {
      const raw = err.response?.data;
      const d = raw?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? JSON.stringify(d) : err.message);
      console.error('[Paraşüt manuel gönder] hata:', raw ?? err.message);
      toast.error(msg || 'Paraşüt gönderimi başarısız');
    } finally {
      setParasutSending(false);
    }
  };

  if (!open) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        fitContent
        className="max-w-4xl w-full p-0 gap-0 overflow-visible max-h-none"
        data-testid="order-detail-modal"
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-800" />
          </div>
        ) : !order ? (
          <div className="py-20 text-center text-gray-500">Siparis bulunamadi</div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-white border-b px-6 py-4">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-lg font-bold">
                      Siparis #{order.order_number || order.id?.slice(0, 8)}
                    </DialogTitle>
                    <p className="text-xs text-gray-500 mt-0.5 font-mono">{order.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!editing ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="order-edit-btn">
                          <FileText className="w-3.5 h-3.5 mr-1" /> Duzenle
                        </Button>
                        {!isSellerView && !isCancelled && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                            onClick={() => setCancelConfirmOpen(true)}
                            data-testid="order-cancel-btn"
                          >
                            <Ban className="w-3.5 h-3.5 mr-1" /> İptal et
                          </Button>
                        )}
                        {!isSellerView && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleteConfirmOpen(true)}
                            data-testid="order-delete-btn"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                          <X className="w-3.5 h-3.5 mr-1" /> Iptal
                        </Button>
                        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="order-save-btn">
                          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </DialogHeader>
            </div>

            <div className="px-6 py-5 space-y-6">
              {isCancelled && (
                <div
                  className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
                  data-testid="order-cancelled-banner"
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <Ban className="w-4 h-4" />
                    Sipariş iptal edildi
                  </div>
                  <div className="mt-1.5 space-y-0.5 text-xs text-red-800">
                    {order.cancelled_at && (
                      <p>Tarih: {formatDateTime(order.cancelled_at)}</p>
                    )}
                    {(order.cancelled_by_name || order.cancelled_by_email) && (
                      <p>
                        İptal eden: {order.cancelled_by_name || order.cancelled_by_email}
                        {order.cancellation_source === 'admin' ? ' (admin)' : ''}
                      </p>
                    )}
                    {order.cancellation_reason && (
                      <p>Not: {order.cancellation_reason}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Status Row */}
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Durum:</span>
                  {editing ? (
                    <Select value={editData.status} onValueChange={v => setEditData(p => ({...p, status: v}))}>
                      <SelectTrigger className="w-36 h-8 text-xs" data-testid="order-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['pending','confirmed','processing','shipped','delivered','cancelled'].map(s => (
                          <SelectItem key={s} value={s}>
                            {s === 'cancelled' ? 'İptal edildi' : s.charAt(0).toUpperCase() + s.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[order.status] || 'bg-gray-100'}`} data-testid="order-status-badge">
                      {order.status === 'cancelled' ? 'İPTAL EDİLDİ' : order.status?.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Odeme:</span>
                  {editing ? (
                    <Select value={editData.payment_status} onValueChange={v => setEditData(p => ({...p, payment_status: v}))}>
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['pending','paid','failed','refunded'].map(s => (
                          <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentStatusColors[order.payment_status] || 'bg-gray-100'}`} data-testid="order-payment-badge">
                      {order.payment_status?.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Tip:</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${order.is_guest_order ? 'bg-orange-100 text-orange-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    {order.is_guest_order ? 'Misafir' : 'Uye'}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Customer Info */}
                  <Section icon={User} title="Musteri Bilgileri">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-0.5">
                      <InfoRow label="Ad Soyad" value={order.customer?.name} />
                      <InfoRow label="E-posta" value={order.customer?.email} />
                      <InfoRow label="Telefon" value={order.customer?.phone || order.shipping_address?.phone} />
                      <InfoRow label="Musteri Tipi" value={order.customer?.type === 'guest' ? 'Misafir' : 'Kayitli Uye'} />
                      {order.customer?.type === 'registered' && (
                        <InfoRow label="Uye ID" value={order.customer.user_id?.slice(0, 12) + '...'} mono />
                      )}
                      <div className="pt-3 mt-2 border-t border-gray-200 space-y-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="w-full justify-center"
                          disabled={emailSending || !order.customer?.email}
                          onClick={handleResendOrderEmail}
                          data-testid="order-resend-email-btn"
                        >
                          {emailSending ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Mail className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {emailSending ? 'Gönderiliyor...' : 'Sipariş bilgilerini e-posta ile gönder'}
                        </Button>
                        {emailSendStatus && (
                          <div
                            className={`flex items-start gap-2 rounded-md px-2.5 py-2 text-xs ${
                              emailSendStatus.success
                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                : 'bg-red-50 text-red-800 border border-red-200'
                            }`}
                            data-testid="order-resend-email-status"
                          >
                            {emailSendStatus.success ? (
                              <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            ) : (
                              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            )}
                            <div className="min-w-0">
                              <p className="font-medium">
                                {emailSendStatus.success ? 'E-posta gönderildi' : 'E-posta gönderilemedi'}
                              </p>
                              {emailSendStatus.email && (
                                <p className="mt-0.5 break-all">{emailSendStatus.email}</p>
                              )}
                              <p className="mt-0.5 opacity-90">{emailSendStatus.message}</p>
                              {emailSendStatus.at && (
                                <p className="mt-1 text-[10px] opacity-70">
                                  {formatDateTime(emailSendStatus.at)}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Section>

                  {/* Shipping Address */}
                  <Section icon={MapPin} title="Teslimat Adresi">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-0.5">
                      <InfoRow label="Ad Soyad" value={order.shipping_address?.full_name} />
                      <InfoRow label="Adres" value={order.shipping_address?.address_line1} />
                      {order.shipping_address?.address_line2 && (
                        <InfoRow label="Adres 2" value={order.shipping_address.address_line2} />
                      )}
                      <InfoRow label="Sehir" value={order.shipping_address?.city} />
                      {order.shipping_address?.state && <InfoRow label="Ilce/Bolge" value={order.shipping_address.state} />}
                      <InfoRow label="Posta Kodu" value={order.shipping_address?.postal_code} />
                      <InfoRow label="Ulke" value={order.shipping_address?.country} />
                      <InfoRow label="Telefon" value={order.shipping_address?.phone} />
                    </div>
                  </Section>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Payment Info */}
                  <Section icon={CreditCard} title="Odeme Bilgileri">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-0.5">
                      <InfoRow
                        label="Odeme Yontemi"
                        value={formatPaymentMethodLabel(resolveOrderPaymentMethod(order))}
                      />
                      <InfoRow label="Odeme Durumu" value={(order.payment_status || order.payment_info?.status || 'pending').toUpperCase()} />
                      {order.payment_info?.session_id && (
                        <InfoRow label="Session ID" value={order.payment_info.session_id.slice(0, 20) + '...'} mono />
                      )}
                      {order.payment_info?.transaction_id && (
                        <InfoRow label="Islem ID" value={order.payment_info.transaction_id} mono />
                      )}
                    </div>
                  </Section>

                  {/* Shipping & Tracking */}
                  <Section icon={Truck} title="Kargo Bilgileri">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      {editing ? (
                        <>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Kargo Firmasi</label>
                            <Input
                              value={editData.carrier}
                              onChange={e => setEditData(p => ({...p, carrier: e.target.value}))}
                              placeholder="Ornek: PTT Kargo, Aras, Yurtici..."
                              className="h-8 text-sm"
                              data-testid="order-carrier-input"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Takip Numarasi</label>
                            <Input
                              value={editData.tracking_number}
                              onChange={e => setEditData(p => ({...p, tracking_number: e.target.value}))}
                              placeholder="Kargo takip numarasi..."
                              className="h-8 text-sm"
                              data-testid="order-tracking-input"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <InfoRow label="Kargo Firmasi" value={order.carrier} />
                          <InfoRow label="Takip No" value={order.tracking_number} />
                          {order.tracking_number && (
                            <a href={`https://www.google.com/search?q=${order.carrier || 'kargo'}+takip+${order.tracking_number}`}
                               target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1">
                              <ExternalLink className="w-3 h-3" /> Kargoyu Takip Et
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </Section>

                  {/* Dates */}
                  <Section icon={Clock} title="Tarihler">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-0.5">
                      <InfoRow label="Olusturulma" value={formatDateTime(order.created_at)} />
                      <InfoRow label="Son Guncelleme" value={formatDateTime(order.updated_at)} />
                      {order.resumed_at && <InfoRow label="Devam Ettirme" value={formatDateTime(order.resumed_at)} />}
                    </div>
                  </Section>

                  {/* Paraşüt muhasebe entegrasyonu */}
                  <Section icon={Receipt} title="Paraşüt">
                    <div className={`rounded-lg p-3 space-y-2 border ${order.parasut_sales_invoice_id ? 'bg-emerald-50/80 border-emerald-200' : 'bg-amber-50/80 border-amber-200'}`}>
                      {order.parasut_sales_invoice_id ? (
                        <>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="text-sm font-medium text-emerald-900">Paraşüt&apos;e aktarıldı</span>
                          </div>
                          <div className="text-xs space-y-1 text-gray-700 pl-6">
                            <InfoRow label="Satış fatura kayıt ID" value={order.parasut_sales_invoice_id} mono />
                            {order.parasut_contact_id && (
                              <InfoRow label="Cari (kişi) ID" value={order.parasut_contact_id} mono />
                            )}
                            <InfoRow label="Senkron zamanı" value={formatDateTime(order.parasut_synced_at)} />
                          </div>
                          <a
                            href="https://uygulama.parasut.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 font-medium pl-6"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Paraşüt panelinde faturaları aç
                          </a>
                        </>
                      ) : (
                        <>
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-900 flex-1 min-w-0">
                              <p className="font-medium">Henüz Paraşüt kaydı yok</p>
                              <p className="text-xs text-amber-800/90 mt-1">
                                Sipariş oluşturulduğunda sunucu Paraşüt&apos;e göndermeyi dener; entegrasyon kapalıysa,
                                eksik ayar varsa veya API hata verdiyse kayıt oluşmaz. Aşağıdan tekrar deneyebilirsiniz.
                              </p>
                              <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-amber-300 bg-white hover:bg-amber-50"
                                  disabled={parasutSending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleParasutSend();
                                  }}
                                  data-testid="order-parasut-send-btn"
                                >
                                  {parasutSending ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                  ) : null}
                                  Paraşüt&apos;e gönder
                                </Button>
                                <span className="text-[10px] text-amber-800/80 leading-tight">
                                  Network: POST .../orders/.../parasut-sync — Konsol: <code className="bg-amber-100/80 px-1 rounded">[Paraşüt manuel gönder]</code>
                                </span>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Section>
                </div>
              </div>

              <Separator />

              {/* Seller Info */}
              {order.seller && (
                <>
                  <Section icon={Store} title="Satici Bilgileri">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-0.5">
                      <InfoRow label="Magaza" value={order.seller.name} />
                      <InfoRow label="E-posta" value={order.seller.email} />
                      <InfoRow label="Satici ID" value={order.seller.id?.slice(0, 12) + '...'} mono />
                    </div>
                  </Section>
                  <Separator />
                </>
              )}

              {/* Products */}
              <Section icon={Package} title={`Urunler (${order.items?.length || 0})`}>
                <div className="space-y-3">
                  {order.items?.map((item, idx) => {
                    const productUrl = item.product_id ? `/product/${item.product_id}` : null;
                    const itemName = item.product_name || item.current_name || 'Urun';
                    const itemContent = (
                      <>
                        <div className="w-16 h-16 rounded-md bg-gray-200 overflow-hidden shrink-0">
                          {(item.image || item.images?.[0]) ? (
                            <img
                              src={item.image || item.images[0]}
                              alt={itemName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Package className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm truncate ${productUrl ? 'text-blue-700 group-hover:underline' : 'text-gray-900'}`}>
                            {itemName}
                          </p>
                          {(item.seller_name || order?.seller?.name) && (
                            <p className="text-xs text-gray-600">Tedarikci: {item.seller_name || order.seller.name}</p>
                          )}
                          {item.brand && <p className="text-xs text-gray-500">Marka: {item.brand}</p>}
                          {item.sku && <p className="text-xs text-gray-500 font-mono">SKU: {item.sku}</p>}
                          {item.category && <p className="text-xs text-gray-500">Kategori: {item.category}</p>}
                          {formatSelectedVariant(item.selected_variant, { includeCodes: true }) ? (
                            <p className="text-xs font-medium text-blue-700 mt-1">
                              {formatSelectedVariant(item.selected_variant, { includeCodes: true })}
                            </p>
                          ) : (item.variants?.length > 0 && (
                            <p className="text-xs text-amber-700 mt-1">
                              Müşteri seçimi bu siparişte kayıtlı değil
                            </p>
                          ))}
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-xs text-gray-500">{item.quantity}x</span>
                            <span className="text-sm font-semibold">{formatPrice(item.price)}</span>
                            {item.quantity > 1 && (
                              <span className="text-xs text-gray-400">= {formatPrice(item.price * item.quantity)}</span>
                            )}
                          </div>
                          {productUrl && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 mt-1">
                              <ExternalLink className="w-3 h-3" />
                              Urun detayina git
                            </span>
                          )}
                        </div>
                      </>
                    );

                    if (productUrl) {
                      return (
                        <Link
                          key={idx}
                          to={productUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-blue-50 hover:border-blue-200 transition-colors"
                          data-testid={`order-item-${idx}`}
                        >
                          {itemContent}
                        </Link>
                      );
                    }

                    return (
                      <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`order-item-${idx}`}>
                        {itemContent}
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Separator />

              {/* Financial Summary */}
              <div className="bg-gray-900 text-white rounded-lg p-4" data-testid="order-financial-summary">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Ara Toplam</span>
                    <span>{formatPrice(order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Vergi (KDV)</span>
                    <span>{formatPrice(order.tax)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Kargo Ucreti</span>
                    <span>{order.shipping_cost > 0 ? formatPrice(order.shipping_cost) : 'Ucretsiz'}</span>
                  </div>
                  <Separator className="bg-gray-700" />
                  <div className="flex justify-between text-base font-bold">
                    <span>Toplam</span>
                    <span className="text-emerald-400">{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>

              {/* Notlar (admin: Admin notları / satıcı: aynı alan) */}
              {editing && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block font-medium">
                    {isSellerView ? 'Notlar' : 'Admin Notlari'}
                  </label>
                  <textarea
                    value={editData.admin_notes}
                    onChange={e => setEditData(p => ({...p, admin_notes: e.target.value}))}
                    placeholder="Siparise not ekleyin..."
                    className="w-full h-20 p-2 text-sm border rounded-md resize-none focus:ring-2 focus:ring-gray-300 focus:outline-none"
                    data-testid="order-notes-input"
                  />
                </div>
              )}
              {!editing && order.admin_notes && (
                <Section icon={FileText} title={isSellerView ? 'Notlar' : 'Admin Notlari'}>
                  <p className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-lg p-3">{order.admin_notes}</p>
                </Section>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {!isSellerView && (
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Siparişi iptal et?</AlertDialogTitle>
            <AlertDialogDescription>
              Sipariş
              {order?.order_number ? ` #${order.order_number}` : ''} iptal edilecek (silinmez).
              Durum İptal edildi olarak işaretlenecek ve müşteriye e-posta gönderilecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-2">
            <label className="text-xs text-gray-500 mb-1 block">İptal notu (opsiyonel)</label>
            <Input
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Örn: stok yok, müşteri talebi..."
              data-testid="order-cancel-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleCancelOrder();
              }}
              disabled={cancelling}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
              data-testid="order-cancel-confirm-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> İptal ediliyor...
                </>
              ) : (
                <>
                  <Ban className="w-3.5 h-3.5 mr-1" /> Evet, iptal et
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}

    {!isSellerView && (
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Siparişi sil?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Sipariş
              {order?.order_number ? ` #${order.order_number}` : ''} kalıcı olarak silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteOrder();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="order-delete-confirm-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Evet, sil
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}
    </>
  );
};

export default OrderDetailModal;
